import { computed, ref, toValue, type MaybeRefOrGetter } from 'vue'
import { STRENGTH_THRESHOLD, POTENTIAL_THRESHOLD } from '../models/question-calculation-config'
import {
  collapseNonSelfAverage,
  maxNonSelfDeviation,
  selfAverage,
  type PerspectiveAverageEntry,
} from '@/core/models/perspective-analysis'

export type QuestionLabels = 'strength' | 'potential' | 'blindspot' | 'hiddenStrength' | 'mixed'

export const iconForQuestionLabel = (label: QuestionLabels) => {
  if (label == 'strength') {
    return 'star_shine'
  }
  if (label == 'potential') {
    return 'trending_up'
  }
  if (label == 'blindspot') {
    return 'visibility_off'
  }
  if (label == 'hiddenStrength') {
    return 'star_half'
  }
  return 'shuffle'
}

export const useQuestionAnalysisNew = <
  T extends { perspectives: ReadonlyArray<PerspectiveAverageEntry> },
>(
  questions: MaybeRefOrGetter<ReadonlyArray<T>>,
  showBlindspots: boolean,
  initialMainFilter?: MaybeRefOrGetter<QuestionLabels | 'all'>,
) => {
  const PENALTY_OVERESTIMATION = 0.5
  const PENALTY_DEVIATION = 0.25
  const PENALTY_SELF_ONLY = 0.1

  const preparedQuestions = computed(() => {
    return toValue(questions).map((question) => {
      const self = selfAverage(question.perspectives)
      const othersAverage = collapseNonSelfAverage(question.perspectives)
      const hasSelf = self != null
      const hasOthers = othersAverage != null
      const hasNoFeedback = !hasSelf && !hasOthers

      let reality = -1

      if (hasOthers) {
        reality = othersAverage
      } else if (hasSelf) {
        reality = Math.max(0, self - PENALTY_SELF_ONLY)
      }

      let overestimationPenalty = 0

      if (hasSelf && hasOthers) {
        overestimationPenalty = Math.max(0, self - reality)
      }

      const deviationPenalty = maxNonSelfDeviation(question.perspectives)

      const score =
        reality -
        overestimationPenalty * PENALTY_OVERESTIMATION -
        deviationPenalty * PENALTY_DEVIATION

      const overestimate = hasSelf ? self - reality : null

      const labels: QuestionLabels[] = []
      if (!hasNoFeedback && showBlindspots) {
        if (reality >= STRENGTH_THRESHOLD) {
          labels.push('strength')
        }
        if (reality <= POTENTIAL_THRESHOLD) {
          labels.push('potential')
        }
        if (hasSelf && hasOthers) {
          if (self - reality > 0.3) {
            labels.push('blindspot')
          }
          if (reality - self > 0.3) {
            labels.push('blindspot')
          }
        }
        if (deviationPenalty > 0.3) {
          labels.push('mixed')
        }
      }

      return {
        ...question,
        average: score,
        score: score,
        labels: labels,
        overestimate: overestimate,
        deviation: deviationPenalty,
      }
    })
  })

  const getTopStrength = (limit?: number) => {
    return preparedQuestions.value
      .filter((question) => question.labels.includes('strength'))
      .sort((a, b) => b.score - a.score)
      .slice(0, limit)
  }

  const getTopPotential = (limit?: number) => {
    return preparedQuestions.value
      .filter((question) => question.labels.includes('potential'))
      .sort((a, b) => a.score - b.score)
      .slice(0, limit)
  }

  const getTopOverestimate = (limit?: number) => {
    return preparedQuestions.value
      .filter(
        (question) =>
          question.labels.includes('blindspot') &&
          question.overestimate &&
          question.overestimate > 0,
      )
      .sort((a, b) => b.overestimate! - a.overestimate!)
      .slice(0, limit)
  }

  const getTopUnderestimate = (limit?: number) => {
    return preparedQuestions.value
      .filter(
        (question) =>
          question.labels.includes('hiddenStrength') &&
          question.overestimate &&
          question.overestimate < 0,
      )
      .sort((a, b) => a.overestimate! - b.overestimate!)
      .slice(0, limit)
  }

  const allQuestions = computed(() => {
    const filtered = preparedQuestions.value.filter((question) => {
      let includeQuestion = true
      if (mainFilter.value == 'strength') {
        includeQuestion = includeQuestion && question.labels.includes('strength')
      }
      if (mainFilter.value == 'potential') {
        includeQuestion = includeQuestion && question.labels.includes('potential')
      }
      if (mainFilter.value == 'blindspot') {
        includeQuestion =
          includeQuestion &&
          (question.labels.includes('blindspot') || question.labels.includes('hiddenStrength'))
      }
      return includeQuestion
    })

    const sorted = [...filtered].sort((a, b) => {
      if (mainFilter.value == 'potential') {
        return a.score - b.score
      }
      return b.score - a.score
    })
    return sorted
  })

  const mainFilter = ref<QuestionLabels | 'all'>(
    initialMainFilter ? toValue(initialMainFilter) : 'all',
  )
  const blindSpotFilter = ref(false)

  const toggleBlindSpotFilter = () => {
    blindSpotFilter.value = !blindSpotFilter.value
  }

  const allQuestionsSorted = (sortBy: QuestionLabels) => {
    return [...preparedQuestions.value].sort((a, b) => {
      if (sortBy === 'potential') {
        return a.score - b.score
      }
      if (sortBy === 'blindspot') {
        return (b.overestimate ?? 0) - (a.overestimate ?? 0)
      }
      if (sortBy === 'hiddenStrength') {
        return (a.overestimate ?? 0) - (b.overestimate ?? 0)
      }
      if (sortBy === 'mixed') {
        return b.deviation - a.deviation
      }
      // strength (and default): highest score = most fitting
      return b.score - a.score
    })
  }

  return {
    allQuestions,
    allQuestionsSorted,
    mainFilter,
    blindSpotFilter,
    toggleBlindSpotFilter,
    getTopStrength,
    getTopPotential,
    getTopOverestimate,
    getTopUnderestimate,
  }
}
